// Picker for photos that are not uploaded yet: selection, local previews via
// object URLs, per-photo captions, and removal. Upload order is selection
// order — the caller turns the array index into the outing_photo `sort`.
import { useEffect, useRef, type ChangeEvent } from "react";
import { ImagePlus, X } from "lucide-react";
import { useT } from "../lib/i18n";
import { TextInput } from "./ui";

export type NewPhoto = { file: File; preview: string; caption: string };

export function PhotoPicker({
  photos,
  onChange,
}: {
  photos: NewPhoto[];
  onChange: (photos: NewPhoto[]) => void;
}) {
  const { t } = useT();
  // Revoke every preview URL on unmount; removal revokes eagerly below.
  const latest = useRef(photos);
  latest.current = photos;
  useEffect(() => () => latest.current.forEach((p) => URL.revokeObjectURL(p.preview)), []);

  function onSelect(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!files.length) return;
    onChange([
      ...photos,
      ...files.map((file) => ({ file, preview: URL.createObjectURL(file), caption: "" })),
    ]);
  }

  function removeAt(index: number) {
    const photo = photos[index];
    if (photo) URL.revokeObjectURL(photo.preview);
    onChange(photos.filter((_, i) => i !== index));
  }

  function setCaption(index: number, caption: string) {
    onChange(photos.map((p, i) => (i === index ? { ...p, caption } : p)));
  }

  return (
    <div className="space-y-2">
      {photos.length > 0 && (
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {photos.map((p, i) => (
            <li key={p.preview} className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow-card">
              <div className="relative">
                <img src={p.preview} alt="" className="aspect-square w-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  aria-label={t("photo.removeAria")}
                  className="absolute right-1 top-1 rounded-full bg-background/90 p-1 text-muted-foreground shadow-card transition-colors hover:text-destructive"
                >
                  <X aria-hidden className="size-3.5" />
                </button>
              </div>
              <div className="p-1.5">
                <TextInput
                  value={p.caption}
                  placeholder={t("photo.caption")}
                  onChange={(e) => setCaption(i, e.target.value)}
                  className="h-7 px-2 text-xs"
                />
              </div>
            </li>
          ))}
        </ul>
      )}
      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-1.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/15">
        <ImagePlus aria-hidden className="size-4" />
        {photos.length ? t("photo.addMore") : t("photo.add")}
        <input type="file" accept="image/*" multiple className="hidden" onChange={onSelect} />
      </label>
    </div>
  );
}
